import { Component, ViewChild, ElementRef, Input, OnChanges, SimpleChanges, AfterViewInit } from '@angular/core';
import Highcharts from 'highcharts';
import { LanguageService } from '../../../../../../../../projects/common/src/app/services/parameters/language/language.service';
import { IExtractionPointDetailList, IProductionByStreetChart } from '../../../../../widget/interfaces/production-by-street';
import { firstLetterUpper } from '../../../../../../../../projects/common/src/app/services/utils/util.service';
import { IShift } from 'src/app/modules/core/interfaces';
import { ParameterHandlerService } from 'projects/common/src/app/services';
import { GoogleAnalyticsService } from 'src/app/modules/core/services/google/google-analytics.service';

@Component({
  selector: 'app-prod-by-extraction-point-chart',
  templateUrl: './prod-by-extraction-point-chart.component.html',
  styleUrls: ['./prod-by-extraction-point-chart.component.scss'],
})
export class ProdByExtractionPointChartComponent implements AfterViewInit {
  @ViewChild('chartTarget', { read: ElementRef, static: false }) public chartTarget: ElementRef;
  @Input() public zoneName: string;
  @Input() public streetName: string;
  @Input() public extractionPointDetailList: IProductionByStreetChart[];
  @Input() public loopByTypeTruck: any;
  @Input() public shiftLabel: string;

  public dataProductionList: any;

  protected chart: Highcharts.Chart;
  constructor(
    protected langService: LanguageService,
    protected parameterService: ParameterHandlerService,
    protected googleAnalyticsService: GoogleAnalyticsService,
  ) {}

  /**
   * After component view init
   */
  public ngAfterViewInit(): void {
    const shift = this.parameterService.parameters.item('shift').value as IShift;
    if (this.extractionPointDetailList) {
      this.dataProductionList = this.setChartConfig(shift, this.extractionPointDetailList);
    }
  }

  /**
   * Function to declare & init chart properties
   *
   * @param extractionPointDetailList data to fill chart view
   */
  protected setChartConfig(shiftId: IShift, dataProdList: any): void {
    const { categories, xtext, ytext, series } = this.defineChartProperties(dataProdList);
    const { tooltipModalHtml } = this;
    const { zoneName } = this;
    const { streetName } = this;
    const shiftTooltipLabel = `${this.langService.message('shift_')} ${shiftId.name}`.toUpperCase();
    const streetLabel = firstLetterUpper(this.langService.message('street'));

    const data = {
      chart: {
        type: 'column',
        backgroundColor: '#F8F8F8',
      },
      title: {
        style: {
          display: 'none',
        },
      },
      credits: {
        enabled: false,
      },
      xAxis: {
        categories,
        title: {
          text: xtext,
          style: {
            fontSize: 12,
            color: '#707070',
            fontWeight: '600',
            fontFamily: 'Open Sans',
          },
        },
        labels: {
          rotation: -45,
          padding: 0,
          step: 1,
          style: {
            color: '#707070',
            fontSize: 10,
          },
        },
      },
      yAxis: {
        min: 0,
        title: {
          text: ytext,
          style: {
            fontSize: 12,
            color: '#707070',
            fontWeight: '600',
            fontFamily: 'Open Sans',
          },
        },
      },
      legend: {
        squareSymbol: false,
        align: 'center',
        verticalAlign: 'top',
        symbolHeight: 0.001,
        symbolWidth: 0.001,
        symbolRadius: 0.001,
        reversed: true,
        margin: 40,
        hideDelay: 0,
        outside: true,
        itemStyle: {
          fontSize: 12,
          fontWeight: '600',
          fontFamily: 'Open Sans',
          color: '#433E3E',
        },
        itemCheckboxStyle: {
          color: '#ffffff',
        },
        useHTML: true,
        labelFormatter(): string {
          return `
          <div style=" height: 20px; display: flex;">
            <svg width="36" height="18" style=" float: left;">
              <rect width="30" height="12" x="2" y="2"
              style="fill: ${this.color}; stroke-width: 2px !important;
              stroke: ${this.color.slice(0, -2)} !important;"/>
            </svg>

            <span style="font-size: 12px; font-family: Open Sans; font-weight: 500;
            color: #433E3E; float: left;">${this.name}</span>
          </div>`;
        },
      },
      tooltip: {
        hideDelay: 0,
        backgroundColor: '#333333',
        borderColor: '#333333',
        borderRadius: 15,
        className: 'tooltip-zindex',
        style: {
          color: 'white',
          fontFamily: 'Open Sans',
          fontSize: '10px',
        },
        useHTML: true,
        formatter(): string {
          const tooltipProps = {
            zoneName,
            streetLabel,
            streetName,
            shiftTooltipLabel,
            color: this.color,
            x: this.x,
            y: this.y,
            series: this.series,
          };
          return tooltipModalHtml(tooltipProps);
        },
      },
      plotOptions: {
        series: {
          marker: {
            enabled: true,
            width: 10,
          },
          stacking: 'normal',
          cursor: 'pointer',
          maxPointWidth: 35,
          dataLabels: {
            enabled: true,
            verticalAlign() {
              return this.y === 0 ? 'bottom' : 'top';
            },
            allowOverlap: false,
            style: {
              color: '#707070',
              textOutline: 'none',
              fontWeight: '600',
              fontSize: '11px',
              fontFamily: 'Open Sans',
            },
            y: -20,
          },
          events: {
            legendItemClick: (e) => {
              e.preventDefault();
            },
          },
        },
      },
      series,
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 500,
            },
            chartOptions: {
              chart: {
                className: 'small-chart',
              },
            },
          },
        ],
      },
    };

    if (this.chartTarget) {
      this.chart = Highcharts.chart(this.chartTarget.nativeElement, data as any, (chart) => {
        if (!dataProdList) {
          const newX = chart.plotWidth / 3 + chart.plotLeft;
          const newY = chart.plotWidth / 2 + chart.plotTop;

          chart.renderer
            .text('La solicitud no arrojó resultados', newX, newY)
            .css({
              color: '#4572A7',
              fontSize: '24px',
            })
            .add();
        }
      });

      window.dispatchEvent(new Event('resize'));
    }
  }

  protected tooltipModalHtml(tooltipProps: any) {
    let tooltipZoneText = '';
    if (tooltipProps.zoneName !== '') {
      tooltipZoneText = `<span> ${tooltipProps.zoneName} - ${tooltipProps.streetLabel} ${tooltipProps.streetName} - ${tooltipProps.x}</span> <br>`;
    } else {
      tooltipZoneText = `<span> ${tooltipProps.streetLabel} ${tooltipProps.streetName} - ${tooltipProps.x}</span> <br>`;
    }
    return `
          <div style="width: 150px;">
            <strong>${tooltipProps.shiftTooltipLabel}</strong> <br>
            ${tooltipZoneText}
            <div style="margin-top: 2px;">
              <svg width="20" height="13" style="float: left; margin-right: 5px; border-radius: 1px;">
                <rect width="20" height="13" style="fill:${tooltipProps.color.slice(0, -2)};"/>
              </svg>
              <span>${tooltipProps.y} ${tooltipProps.series.name}</span>
          </div>`;
  }

  /**
   * Defined chart config
   *
   * @param dataProdList
   */
  protected defineChartProperties(dataProdList: any): any {
    const categories = dataProdList.map((production) => production.zoneName);
    const xtext = this.langService.message('ditch').toUpperCase();
    const ytext = this.langService.message('shelves').toUpperCase();
    const series = this.buildSeriesForChart(dataProdList);
    return { categories, xtext, ytext, series };
  }

  /**
   * Function to build series for display chart values
   *
   * @param extractionPointDetailList data to fill chart view
   */
  protected buildSeriesForChart(extractionPointDetailList: IExtractionPointDetailList[]): any[] {
    return [
      {
        name: this.langService.message('notscheduleddoneloops'),
        data: extractionPointDetailList.map((production) => production.notScheduledDoneLoops),
        color: '#29BCDE80',
        borderColor: '#29BCDE',
        borderWidth: 2,
        states: {
          hover: {
            color: '#29BCDE',
          },
        },
      },
      {
        name: this.langService.message('remaindertodone'),
        data: extractionPointDetailList.map((production) => production.remainderToDone),
        color: '#D3721880',
        borderColor: '#D37218',
        borderWidth: 2,
        states: {
          hover: {
            color: '#D37218',
          },
        },
      },
      {
        name: this.langService.message('todone'),
        data: extractionPointDetailList.map((production) => production.toDone),
        color: '#AFAFAF80',
        borderColor: '#AFAFAF',
        borderWidth: 2,
        states: {
          hover: {
            color: '#AFAFAF',
          },
        },
      },
      {
        name: this.langService.message('total-shelves'), // scheduledDone
        data: extractionPointDetailList.map((production) => production.scheduledDone),
        color: '#22D98380',
        borderColor: '#22D983',
        borderWidth: 2,
        states: {
          hover: {
            color: '#22D983',
          },
        },
      },
    ];
  }
}
